import React from 'react';

/**
 * Who touches a multi-box product, and where its boxes come from.
 *
 * Inline rather than an imported .svg so the nodes can use the page's
 * own custom properties — the two families here are the same amber and
 * blue the chain table and box types already use.
 *
 * Two groups, two colours, deliberately: sources on the left, people on
 * the right, and one spine between them. That spine is the argument the
 * page makes in decision 4 — everybody reads the same carton timeline,
 * each inside the tool they already use.
 */
const SOURCES = [
  { label: 'The factory', sub: 'packs and labels' },
  { label: 'A prep center', sub: 'relabels, palletizes' },
  { label: 'A distributor', sub: 'labels boxes it didn’t pack' },
  { label: 'Customers', sub: 'returns, sometimes partial' },
  { label: 'The seller, later', sub: 'replacement boxes' },
];

const PEOPLE = [
  { label: 'Seller', sub: 'ops manager · seller portal' },
  { label: 'Supplier', sub: 'packer · shared label pack' },
  { label: 'Receiving associate', sub: 'scanner' },
  { label: 'Operations manager', sub: 'ops dashboard' },
  { label: 'Customer service', sub: 'service console' },
  { label: 'Carrier', sub: 'driver · delivery scan' },
  { label: 'Customer', sub: 'order and returns' },
];

const SRC_X = 18;
const SRC_W = 212;
const SRC_H = 58;
const PPL_X = 846;
const PPL_W = 256;
const PPL_H = 64;
const SPINE = { x: 414, y: 296, w: 292, h: 128 };
const SPINE_CY = SPINE.y + SPINE.h / 2;

const srcY = (i) => 64 + i * 130;
const pplY = (i) => 48 + i * 100;

const Node = ({ x, y, w, h, label, sub, fill, stroke, text }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} rx={h / 2} fill={fill} stroke={stroke} strokeWidth="1.5" />
    <text x={x + w / 2} y={sub ? y + h / 2 - 4 : y + h / 2 + 5}
          textAnchor="middle" fontSize="15" fontWeight="650" fill={text}>{label}</text>
    {sub && (
      <text x={x + w / 2} y={y + h / 2 + 15} textAnchor="middle"
            fontSize="11.5" fill={text} opacity="0.75">{sub}</text>
    )}
  </g>
);

const UserNetwork = () => (
  <svg className="multibox-network" viewBox="0 0 1120 712" role="img"
       aria-labelledby="net-title net-desc">
    <title id="net-title">Who touches a multi-box product</title>
    <desc id="net-desc">
      Five sources — the factory, a prep center, a distributor, customer returns and later
      seller replacements — all write to one carton timeline. Seven people read that same
      timeline inside their own tool: the seller, the supplier, the receiving associate, the
      operations manager, customer service, the carrier and the customer.
    </desc>

    <rect width="1120" height="712" rx="12" fill="var(--mb-surface)" />

    {/* source -> spine */}
    {SOURCES.map((s, i) => {
      const y = srcY(i) + SRC_H / 2;
      return (
        <path key={`sl${s.label}`}
              d={`M ${SRC_X + SRC_W} ${y} C ${SRC_X + SRC_W + 90} ${y}, ${SPINE.x - 90} ${SPINE_CY}, ${SPINE.x} ${SPINE_CY}`}
              fill="none" stroke="var(--mb-h3)" strokeWidth="1.5" opacity="0.45" />
      );
    })}

    {/* spine -> person */}
    {PEOPLE.map((p, i) => {
      const y = pplY(i) + PPL_H / 2;
      return (
        <path key={`pl${p.label}`}
              d={`M ${SPINE.x + SPINE.w} ${SPINE_CY} C ${SPINE.x + SPINE.w + 70} ${SPINE_CY}, ${PPL_X - 70} ${y}, ${PPL_X} ${y}`}
              fill="none" stroke="var(--mb-h1)" strokeWidth="1.5" opacity="0.45" />
      );
    })}

    <text x={SRC_X + 8} y="30" fontSize="11.5" fontWeight="700" letterSpacing="1.4"
          fill="var(--mb-h3)">WHERE BOXES COME FROM</text>
    <text x={PPL_X + 8} y="30" fontSize="11.5" fontWeight="700" letterSpacing="1.4"
          fill="var(--mb-h1)">WHO READS IT</text>

    {SOURCES.map((s, i) => (
      <Node key={s.label} x={SRC_X} y={srcY(i)} w={SRC_W} h={SRC_H}
            label={s.label} sub={s.sub}
            fill="var(--mb-h3-soft)" stroke="var(--mb-h3)" text="var(--mb-h3)" />
    ))}

    {/* the spine */}
    <rect x={SPINE.x} y={SPINE.y} width={SPINE.w} height={SPINE.h} rx="18"
          fill="var(--mb-accent)" />
    <text x={SPINE.x + SPINE.w / 2} y={SPINE.y + 50} textAnchor="middle"
          fontSize="19" fontWeight="700" fill="#fff">One carton timeline</text>
    <text x={SPINE.x + SPINE.w / 2} y={SPINE.y + 76} textAnchor="middle"
          fontSize="12.5" fill="#fff" opacity="0.82">every label, notice and scan</text>
    <text x={SPINE.x + SPINE.w / 2} y={SPINE.y + 94} textAnchor="middle"
          fontSize="12.5" fill="#fff" opacity="0.82">writes to it</text>

    {PEOPLE.map((p, i) => (
      <Node key={p.label} x={PPL_X} y={pplY(i)} w={PPL_W} h={PPL_H}
            label={p.label} sub={p.sub}
            fill="var(--mb-h1-soft)" stroke="var(--mb-h1)" text="var(--mb-h1)" />
    ))}
  </svg>
);

export default UserNetwork;
